import React, { useState, useEffect } from "react";
import Label from "../fields/Label";
import { useTransientMessage } from "../../hooks/useTransientMessage";
import { MdFitnessCenter } from "react-icons/md";
import ModalShell from "../modals/ModalShell";
import { MuscleGroup, MuscleGroupAssignment } from "../../types/exercise";
import Button from "../common/Button";

interface MuscleGroupManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (muscleGroups: MuscleGroupAssignment[]) => Promise<void>;
  availableMuscleGroups: MuscleGroup[];
  currentPrimary?: MuscleGroup[];
  currentSecondary?: MuscleGroup[];
  loading?: boolean;
  /** Request error from the owning page; shown in the modal footer banner. */
  error?: string | null;
}

const MuscleGroupManagementModal: React.FC<MuscleGroupManagementModalProps> = ({
  isOpen,
  onClose,
  onSave,
  availableMuscleGroups,
  currentPrimary = [],
  currentSecondary = [],
  loading = false,
  error: responseError = null,
}) => {
  const [primaryMuscleGroupIds, setPrimaryMuscleGroupIds] = useState<number[]>(
    []
  );
  const [secondaryMuscleGroupIds, setSecondaryMuscleGroupIds] = useState<
    number[]
  >([]);
  const [error, setError] = useTransientMessage(3000);
  const [saving, setSaving] = useState(false);

  // Sort muscle groups alphabetically by name
  const sortedMuscleGroups = [...availableMuscleGroups].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  useEffect(() => {
    if (isOpen) {
      // Initialize with current values
      setPrimaryMuscleGroupIds(currentPrimary.map((mg) => mg.id));
      setSecondaryMuscleGroupIds(currentSecondary.map((mg) => mg.id));
      setError(null);
    }
  }, [isOpen, currentPrimary, currentSecondary]);

  const handlePrimaryToggle = (muscleGroupId: number) => {
    setPrimaryMuscleGroupIds((prev) => {
      if (prev.includes(muscleGroupId)) {
        return prev.filter((id) => id !== muscleGroupId);
      } else {
        // Check if we're at the limit
        if (prev.length >= 5) {
          setError("Maximum 5 primary muscle groups allowed");
          return prev;
        }
        return [...prev, muscleGroupId];
      }
    });
  };

  const handleSecondaryToggle = (muscleGroupId: number) => {
    setSecondaryMuscleGroupIds((prev) => {
      if (prev.includes(muscleGroupId)) {
        return prev.filter((id) => id !== muscleGroupId);
      } else {
        // Check if we're at the limit
        if (prev.length >= 5) {
          setError("Maximum 5 secondary muscle groups allowed");
          return prev;
        }
        return [...prev, muscleGroupId];
      }
    });
  };

  const handleSave = async () => {
    setError(null);

    // Validate at least one muscle group
    if (
      primaryMuscleGroupIds.length === 0 &&
      secondaryMuscleGroupIds.length === 0
    ) {
      setError("Please select at least one muscle group");
      return;
    }

    // Build muscle groups array
    const muscleGroups: MuscleGroupAssignment[] = [];

    primaryMuscleGroupIds.forEach((id) => {
      muscleGroups.push({
        muscle_group_id: id,
        type: "primary",
      });
    });

    secondaryMuscleGroupIds.forEach((id) => {
      muscleGroups.push({
        muscle_group_id: id,
        type: "secondary",
      });
    });

    setSaving(true);
    try {
      await onSave(muscleGroups);
    } catch (error) {
      setError("Failed to save muscle groups");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Muscle Groups"
      icon={
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-biafit-primary">
          <MdFitnessCenter className="h-6 w-6 text-white" />
        </div>
      }
      size="2xl"
      layout="fixed"
      message={
        error || responseError
          ? { text: error || responseError, type: "error" }
          : null
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            loadingLabel="Saving..."
            disabled={loading}
          >
            Save Changes
          </Button>
        </>
      }
    >
      {/* Body */}
      <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
        {/* Primary Muscle Groups */}
        <div className="mb-6">
          <Label>
            Primary Muscle Groups
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              ({primaryMuscleGroupIds.length}/5 selected)
            </span>
          </Label>
          <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-3 dark:border-gray-700">
            {sortedMuscleGroups.length === 0 ? (
              <div className="py-4 text-center text-gray-500 dark:text-gray-400">
                Loading muscle groups...
              </div>
            ) : (
              sortedMuscleGroups.map((mg) => (
                <label
                  key={mg.id}
                  className={`flex cursor-pointer items-center rounded-md p-2 hover:bg-gray-50 dark:hover:bg-navy-700 ${
                    secondaryMuscleGroupIds.includes(mg.id)
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={primaryMuscleGroupIds.includes(mg.id)}
                    onChange={() => handlePrimaryToggle(mg.id)}
                    disabled={
                      secondaryMuscleGroupIds.includes(mg.id) ||
                      loading ||
                      saving
                    }
                    className="h-4 w-4 rounded border-gray-300 text-biafit-primary focus:ring-biafit-primary"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {mg.name}
                    {secondaryMuscleGroupIds.includes(mg.id) && (
                      <span className="ml-2 text-xs text-gray-500">
                        (selected as secondary)
                      </span>
                    )}
                  </span>
                </label>
              ))
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Select up to 5 primary muscle groups (cannot select secondary as
            primary)
          </p>
        </div>

        {/* Secondary Muscle Groups */}
        <div>
          <Label>
            Secondary Muscle Groups
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              ({secondaryMuscleGroupIds.length}/5 selected)
            </span>
          </Label>
          <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-3 dark:border-gray-700">
            {sortedMuscleGroups.length === 0 ? (
              <div className="py-4 text-center text-gray-500 dark:text-gray-400">
                Loading muscle groups...
              </div>
            ) : (
              sortedMuscleGroups.map((mg) => (
                <label
                  key={mg.id}
                  className={`flex cursor-pointer items-center rounded-md p-2 hover:bg-gray-50 dark:hover:bg-navy-700 ${
                    primaryMuscleGroupIds.includes(mg.id)
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={secondaryMuscleGroupIds.includes(mg.id)}
                    onChange={() => handleSecondaryToggle(mg.id)}
                    disabled={
                      primaryMuscleGroupIds.includes(mg.id) || loading || saving
                    }
                    className="h-4 w-4 rounded border-gray-300 text-biafit-primary focus:ring-biafit-primary"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {mg.name}
                    {primaryMuscleGroupIds.includes(mg.id) && (
                      <span className="ml-2 text-xs text-gray-500">
                        (selected as primary)
                      </span>
                    )}
                  </span>
                </label>
              ))
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Select up to 5 secondary muscle groups (cannot select primary as
            secondary)
          </p>
        </div>
      </div>
    </ModalShell>
  );
};

export default MuscleGroupManagementModal;
